// API client for the Sales Performance Dashboard.
//
// The two failure states the page must tell apart carry their own error types:
// a report that has not synced yet is a wait, a missing Amazon connection is a
// different fix entirely, and neither should render as an empty chart.
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const BASE = `${API_URL}/seller-intelligence/sales-performance`;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export class ReportUnavailableError extends Error {
  constructor(message) {
    super(message);
    this.reportUnavailable = true;
  }
}

export class NotConnectedError extends Error {
  constructor(message) {
    super(message);
    this.notConnected = true;
  }
}

const call = async (url, options = {}) => {
  const res = await fetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (res.status === 409 && data.error === 'report_unavailable') {
    throw new ReportUnavailableError(data.message);
  }
  if (res.status === 400 && data.error === 'amazon_not_connected') {
    throw new NotConnectedError(data.message);
  }
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
};

// SF1 and SF2 are one component, so one call returns both series and the metric
// toggle switches between them without going back to the network.
export const getTrend = (token, { start, end, granularity } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (granularity) params.set('granularity', granularity);
  const query = params.toString();
  return call(`${BASE}/trend${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF3 — the KPI strip. No granularity: average order value describes the whole
// window rather than a bucket.
export const getKpis = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${BASE}/kpis${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF4 — Top Performing SKUs. Ranked by revenue; no granularity, because the
// table describes the window rather than a bucket.
export const getTopSkus = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${BASE}/top-skus${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF5 — Bottom Performing SKUs. Products that sold nothing are counted rather
// than ranked, so the rows are the lowest sellers that actually sold.
export const getBottomSkus = (token, { start, end } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  const query = params.toString();
  return call(`${BASE}/bottom-skus${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};

// SF6 — Revenue by Category and Brand. One endpoint, one grouping key; asking
// for category returns the brand view with the reason, rather than failing.
export const getRevenueByGroup = (token, { start, end, groupBy } = {}) => {
  const params = new URLSearchParams();
  if (start) params.set('start', start);
  if (end) params.set('end', end);
  if (groupBy) params.set('group_by', groupBy);
  const query = params.toString();
  return call(`${BASE}/revenue-by-group${query ? `?${query}` : ''}`, {
    headers: authHeaders(token),
  });
};
